import { useMemo, useState } from 'react';
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { DataTable, DataTableCell, DataTableHead, DataTableRow } from '@/components/table/DataTable';
import { SegmentedFilter } from '@/components/table/SegmentedFilter';
import { StatTile } from '@/components/shell/StatTile';
import {
  AI_WINDOWS,
  type AiGroupBy,
  type AiRoute,
  type AiUsageRow,
  type AiWindow,
  aiApi,
  durationLabel,
  num,
  seriesLabel,
  totalsOf,
  windowRange,
} from '@/lib/aiAdmin';
import { chartAxisTick, chartBarCursor, chartGridStroke, chartLineCursor, chartSeriesColor, chartTooltipProps } from '@/lib/chartTheme';
import { formatCount, formatUsd } from '@/lib/format';

import { CENTER, TITLE, columns, Loading, LoadFailed, SectionLabel, useLoad } from './common';

const WINDOW_OPTIONS = AI_WINDOWS.map((id) => ({ id, label: { '24h': '24 hours', '7d': '7 days', '30d': '30 days' }[id] }));
const BREAKDOWNS: Array<{ id: AiGroupBy; label: string }> = [
  { id: 'provider', label: 'Provider' },
  { id: 'model', label: 'Model' },
  { id: 'organization', label: 'Organization' },
];

const tokens = (row: { inputTokens: number; outputTokens: number }) => `${formatCount(row.inputTokens)} / ${formatCount(row.outputTokens)}`;

function Chart({ label, children }: { label: string; children: React.ReactElement }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div role="img" aria-label={label} className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          {children}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/** Calls, errors, refusals, tokens, cost and duration for 24 hours, 7 days and 30 days, and where they come from. */
export function OverviewTab({ routes }: { routes: AiRoute[] | undefined }) {
  const [window, setWindow] = useState<AiWindow>('7d');
  const [by, setBy] = useState<AiGroupBy>('provider');
  const summary = useLoad(
    () => Promise.all(AI_WINDOWS.map((w) => aiApi.usage(windowRange(w).from, windowRange(w).to, 'provider'))),
    [],
  );
  const range = useMemo(() => windowRange(window), [window]);
  const series = useLoad(() => aiApi.usage(range.from, range.to, range.series), [range]);
  const breakdown = useLoad(() => aiApi.usage(range.from, range.to, by), [range, by]);

  const health = (routes ?? []).reduce(
    (sum, route) => ({ errors: sum.errors + num(route.health?.errors), failovers: sum.failovers + num(route.health?.failovers), on: sum.on + (route.enabled === true ? 1 : 0) }),
    { errors: 0, failovers: 0, on: 0 },
  );
  const seriesRows = (series.data?.rows ?? []).map((row) => ({
    label: seriesLabel(row.key, range.series),
    ok: Math.max(0, num(row.calls) - num(row.errors) - num(row.refusals)),
    errors: num(row.errors),
    refusals: num(row.refusals),
    cost: num(row.costUsd),
  }));
  const breakdownRows = [...(breakdown.data?.rows ?? [])].sort((a, b) => num(b.calls) - num(a.calls));

  return (
    <div className="space-y-8">
      <section>
        <SectionLabel>Last hour</SectionLabel>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatTile label="Failovers" value={routes ? formatCount(health.failovers) : '…'} detail="Calls that moved on" />
          <StatTile label="Route errors" value={routes ? formatCount(health.errors) : '…'} detail="Calls with status failed" />
          <StatTile label="Routes on" value={routes ? `${health.on} of ${routes.length}` : '…'} detail="Off routes get no calls" />
        </div>
      </section>

      <section>
        <SectionLabel>Totals</SectionLabel>
        {summary.error ? (
          <LoadFailed message={summary.error} onRetry={summary.reload} />
        ) : !summary.data ? (
          <Loading label="Loading totals" />
        ) : (
          <>
            <DataTable>
              <DataTableHead columns={columns(['Window', 'Calls', 'Errors', 'Refusals', 'Tokens in / out', 'Cost', 'p50', 'p95'])} />
              <tbody>
                {summary.data.map((result, i) => {
                  const total = totalsOf(result.rows ?? []);
                  return (
                    <DataTableRow key={AI_WINDOWS[i]}>
                      <DataTableCell className={TITLE}>{WINDOW_OPTIONS[i]?.label}</DataTableCell>
                      <DataTableCell className={CENTER}>{formatCount(total.calls)}</DataTableCell>
                      <DataTableCell className={CENTER}>{formatCount(total.errors)}</DataTableCell>
                      <DataTableCell className={CENTER}>{formatCount(total.refusals)}</DataTableCell>
                      <DataTableCell className={CENTER}>{tokens(total)}</DataTableCell>
                      <DataTableCell className={CENTER}>{formatUsd(total.costUsd)}</DataTableCell>
                      <DataTableCell className={CENTER}>{durationLabel(total.p50)}</DataTableCell>
                      <DataTableCell className={CENTER}>{durationLabel(total.p95)}</DataTableCell>
                    </DataTableRow>
                  );
                })}
              </tbody>
            </DataTable>
            <p className="mt-2 text-xs text-muted-foreground">Errors are calls with the status failed. Refusals are calls with the status denied or refused. ai gives p50 and p95 for each provider, so the total shows the slowest provider.</p>
          </>
        )}
      </section>

      <section className="space-y-4">
        <SectionLabel actions={<SegmentedFilter label="Time window" options={WINDOW_OPTIONS} value={window} onChange={setWindow} />}>
          Usage over time
        </SectionLabel>
        {series.error ? (
          <LoadFailed message={series.error} onRetry={series.reload} />
        ) : !series.data ? (
          <Loading label="Loading the chart" />
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div>
              <h3 className="mb-2 text-xs font-medium text-muted-foreground">Calls by {range.series}</h3>
            <Chart label="Calls over time, split into ok, errors and refusals">
              <BarChart data={seriesRows}>
                <CartesianGrid stroke={chartGridStroke} vertical={false} />
                <XAxis dataKey="label" tick={chartAxisTick} tickLine={false} axisLine={false} minTickGap={24} />
                <YAxis tick={chartAxisTick} tickLine={false} axisLine={false} width={40} allowDecimals={false} />
                <Tooltip {...chartTooltipProps} cursor={chartBarCursor} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar isAnimationActive={false} dataKey="ok" name="Ok" stackId="calls" fill={chartSeriesColor(1)} />
                <Bar isAnimationActive={false} dataKey="errors" name="Errors" stackId="calls" fill={chartSeriesColor(4)} />
                <Bar isAnimationActive={false} dataKey="refusals" name="Refusals" stackId="calls" fill={chartSeriesColor(2)} radius={[2, 2, 0, 0]} />
              </BarChart>
            </Chart>
            </div>
            <div>
              <h3 className="mb-2 text-xs font-medium text-muted-foreground">Cost by {range.series}</h3>
            <Chart label="Cost over time in US dollars">
              <AreaChart data={seriesRows}>
                <CartesianGrid stroke={chartGridStroke} vertical={false} />
                <XAxis dataKey="label" tick={chartAxisTick} tickLine={false} axisLine={false} minTickGap={24} />
                <YAxis tick={chartAxisTick} tickLine={false} axisLine={false} width={48} tickFormatter={(value: number) => formatUsd(value)} />
                <Tooltip {...chartTooltipProps} cursor={chartLineCursor} formatter={(value) => formatUsd(Number(value))} />
                <Area isAnimationActive={false} type="monotone" dataKey="cost" name="Cost" stroke={chartSeriesColor(0)} fill={chartSeriesColor(0)} fillOpacity={0.18} strokeWidth={1.5} />
              </AreaChart>
            </Chart>
            </div>
          </div>
        )}
      </section>

      <section className="space-y-4">
        <SectionLabel actions={<SegmentedFilter label="Group by" options={BREAKDOWNS} value={by} onChange={setBy} />}>
          Calls by {by}
        </SectionLabel>
        {breakdown.error ? (
          <LoadFailed message={breakdown.error} onRetry={breakdown.reload} />
        ) : !breakdown.data ? (
          <Loading label="Loading the breakdown" />
        ) : (
          <>
            <Chart label={`Calls by ${by}`}>
              <BarChart data={breakdownRows.slice(0, 8).map((row: AiUsageRow) => ({ key: row.key || 'none', calls: num(row.calls) }))} layout="vertical" margin={{ left: 8 }}>
                <CartesianGrid stroke={chartGridStroke} horizontal={false} />
                <XAxis type="number" tick={chartAxisTick} tickLine={false} axisLine={false} allowDecimals={false} />
                <YAxis type="category" dataKey="key" tick={chartAxisTick} tickLine={false} axisLine={false} width={150} />
                <Tooltip {...chartTooltipProps} cursor={chartBarCursor} />
                <Bar isAnimationActive={false} dataKey="calls" name="Calls" fill={chartSeriesColor(1)} radius={[0, 2, 2, 0]} />
              </BarChart>
            </Chart>
            <DataTable>
              <DataTableHead columns={columns([BREAKDOWNS.find((b) => b.id === by)?.label ?? 'Group', 'Calls', 'Errors', 'Refusals', 'Tokens in / out', 'Cost', 'p50', 'p95'])} />
              <tbody>
                {breakdownRows.map((row) => (
                  <DataTableRow key={row.key ?? 'none'}>
                    <DataTableCell className={`${TITLE} max-w-[16rem] truncate`}>{row.key || 'none'}</DataTableCell>
                    <DataTableCell className={CENTER}>{formatCount(num(row.calls))}</DataTableCell>
                    <DataTableCell className={CENTER}>{formatCount(num(row.errors))}</DataTableCell>
                    <DataTableCell className={CENTER}>{formatCount(num(row.refusals))}</DataTableCell>
                    <DataTableCell className={CENTER}>{tokens({ inputTokens: num(row.inputTokens), outputTokens: num(row.outputTokens) })}</DataTableCell>
                    <DataTableCell className={CENTER}>{formatUsd(num(row.costUsd))}</DataTableCell>
                    <DataTableCell className={CENTER}>{durationLabel(row.p50LatencyMs)}</DataTableCell>
                    <DataTableCell className={CENTER}>{durationLabel(row.p95LatencyMs)}</DataTableCell>
                  </DataTableRow>
                ))}
                {breakdownRows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-sm text-muted-foreground">No calls in this window.</td>
                  </tr>
                ) : null}
              </tbody>
            </DataTable>
          </>
        )}
      </section>
    </div>
  );
}
